import {
  // ElementModels,
  Elements,
} from '@kontent-ai/delivery-sdk';
import { FC, JSX } from 'react';
import { PortableTextBlock } from '@portabletext/types';
import { Link } from 'react-router-dom';
import { resolveContentLink } from '../Utilities/ContentLinks';
import { CPCUFlashCardList } from './VirtualFlashCard';
import KeywordHover from './KeywordHover';
import DigitalAssetImageBorder from './DigitalAssetImageBorder';
import CustomAccordion from './Accordion';
import AccordionType from './AccordionType';
import HeroUnit from './HeroUnit';
import CarouselSetup from './CarouselSetup';
import QuizExercise from './Quiz/QuizExercise';
import CallToAction from './CallToAction';
import DigitalAssetVideoBorder from './DigitalAssetVideoBorder';
import InteractiveVideoBorder from './InteractiveSingleVideoBorder';
import InteractiveBranchingBorder from './InteractiveBranchingBorder';
import EmbeddedAudio from './DigitalAssetAudio';
import { PortableText } from '@portabletext/react';
import { transformToPortableText } from '@kontent-ai/rich-text-resolver';
import { PortableTextReactResolvers } from '@kontent-ai/rich-text-resolver/dist/esnext/src/utils/resolution/react';
import { sanitizeFirstChildText } from '../Utilities/Anchors';
import FramelessDigitalAsset from './FramelessDigitalAsset';
import SortableExercise from './SortingSequencingExercise/SortableExercise';
import DragDropExercise from './DragDropMatchingExercise/DragDropExercise';
import Viewer from './Hotspot/Viewer';

type ElementProps = Readonly<{
  element: Elements.RichTextElement;
  isInsideTable: boolean;
}>;

export const RichTextBodyResolvers = (
  element: Elements.RichTextElement,
  _isElementInsideTable: boolean = false,
  _componentIndex = 0,
): PortableTextReactResolvers => ({
  types: {
    image: ({ value }: { value: any }): JSX.Element | null => {
      const asset = element.images.find(i => i.imageId === value.asset._ref);
      if (!asset) {
        throw new Error(`Asset ${value.asset._ref} not found.`);
      }

      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <span className="img-responsive center-block">
            <img
              src={value.asset.url}
              alt={asset.description ?? 'image'}
              width={asset.width ?? undefined}
              height={asset.height ?? undefined}
            />
          </span>
        </div>
      );
    },
    table: ({ value }: { value: any }): JSX.Element => {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingBottom: '16px',
          }}
        >
          <table
            className="table-auto border-collapse border border-gray-600"
            style={{ border: '1px solid #4B5563', margin: '0 auto' }}
          >
            <tbody>
              {value.rows.map((r: any) => (
                <tr
                  key={r._key}
                  className="border border-gray-600 p2"
                  style={{ border: '1px solid #4B5563' }}
                >
                  {r.cells.map((c: any) => (
                    <td
                      key={c._key}
                      className="border border-gray-600 p-2 text-left"
                      style={{
                        border: '1px solid #4B5563',
                        textAlign: 'left',
                        paddingRight: '4px',
                        paddingLeft: '8px',
                      }}
                    >
                      {c.content && c.content.length > 0 ? (
                        <RichTextValue
                          isInsideTable
                          value={c.content.map((content: any) => ({
                            ...content,
                            children: content.children || [],
                          }))}
                          element={element}
                        />
                      ) : (
                        <span>&nbsp;</span> // Render a non-breaking space if the cell is empty
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    },
    componentOrItem: ({ value }: { value: any }): JSX.Element => {
      const linkedItem = element.linkedItems.find(
        i => i.system.codename === value.componentOrItem._ref,
      );

      if (!linkedItem) {
        throw new Error(
          'Component item not found, probably not enough depth requested.',
        );
      }

      const contentItemType = linkedItem ? linkedItem.system.type : '';
      switch (contentItemType) {
        case 'digital_asset___image': {
          return <DigitalAssetImageBorder element={linkedItem?.elements} />;
        }
        case 'digital_asset___video': {
          return <DigitalAssetVideoBorder element={linkedItem?.elements} />;
        }
        case 'interactive_branching___video': {
          return <InteractiveVideoBorder element={linkedItem?.elements} />;
        }
        case 'interactive_branching___multiple_videos': {
          return <InteractiveBranchingBorder element={linkedItem?.elements} />;
        }
        case 'embedded_audio': {
          return (
            <div style={{ paddingBottom: '5px' }}>
              <EmbeddedAudio element={linkedItem?.elements} />
            </div>
          );
        }
        case 'frameless_digital_asset': {
          return (
            <div style={{ paddingBottom: '5px' }}>
              <FramelessDigitalAsset element={linkedItem?.elements} />
            </div>
          );
        }
        case 'virtual_flashcard': {
          return <CPCUFlashCardList element={linkedItem?.elements} />;
        }
        case 'accordion': {
          return <CustomAccordion element={linkedItem?.elements} />;
        }
        case 'accordion_type': {
          return (
            <AccordionType
              title={linkedItem?.elements.title.value}
              accordionTypeStyling={
                linkedItem?.elements.accordion_type_styling?.value[0].codename
              }
              body={linkedItem?.elements.body as Elements.RichTextElement}
              linkedItems={
                linkedItem?.elements
                  .accordion_items as Elements.LinkedItemsElement
              }
            ></AccordionType>
          );
        }
        case 'call_to_action': {
          return <CallToAction element={linkedItem?.elements} />;
        }
        case 'hero_image': {
          return <HeroUnit element={linkedItem?.elements} />;
        }
        case 'carousel': {
          return <CarouselSetup element={linkedItem?.elements} />;
        }
        case 'sorting_sequencing_exercise': {
          return <SortableExercise element={linkedItem?.elements} />;
        }
        case 'quiz_exercise': {
          return <QuizExercise element={linkedItem?.elements} />;
        }
        case 'fill_in_the_blank_question': // ONE QUESTION "QUIZ"
        case 'multiple_choice_question': {
          let tempQuizElement: {
            elements: {
              type: string;
              questions: {
                linkedItems: (typeof linkedItem)[];
                type: string;
              };
              title?: {
                value: string;
              };
            };
          } = {
            elements: {
              type: 'single_question_quiz_exercise',
              questions: {
                linkedItems: [linkedItem],
                type: 'modular_content',
              },
            },
          };

          // Add title if it exists in the linkedItem
          if (linkedItem?.elements?.title?.value) {
            tempQuizElement.elements.title = {
              value: linkedItem.elements.title.value,
            };
          }

          return <QuizExercise element={tempQuizElement.elements} />;
        }
        case 'flashcard': {
          let tempWrapperElement: {
            elements: {
              type: string;
              flashcards: {
                linkedItems: (typeof linkedItem)[];
                type: string;
              };
              title?: {
                value: string;
              };
            };
          } = {
            elements: {
              type: 'single_flashcard_wrapper',
              flashcards: {
                linkedItems: [linkedItem],
                type: 'modular_content',
              },
            },
          };

          // Add title if it exists in the linkedItem
          if (linkedItem?.elements?.title?.value) {
            tempWrapperElement.elements.title = {
              value: linkedItem.elements.title.value,
            };
          }
          return <CPCUFlashCardList element={tempWrapperElement?.elements} />;
        }
        case 'click_to_reveal_hotspot': {
          return <Viewer element={linkedItem?.elements} />;
        }
        case 'drag_and_drop_exercise': {
          return <DragDropExercise element={linkedItem?.elements} />;
        }
        default:
          return <div>Content item not supported</div>;
      }
    },
  },
  marks: {
    contentItemLink: ({
      value,
      children,
    }: {
      value: any;
      children: React.ReactNode;
    }): JSX.Element => {
      const link = element.links.find(
        l => l.linkId === value?.contentItemLink?._ref,
      );
      if (!link) {
        throw new Error(
          'Cannot find link reference in links. This should never happen.',
        );
      }

      const path = resolveContentLink(link);

      switch (link?.type) {
        case 'glossary_terms':
        case 'keyword_hover':
          return (
            <KeywordHover
              linkId={link.linkId}
              keyterm={children?.toString() || ''}
            />
          );
        default:
          return <Link to={path}>{children}</Link>;
      }
    },
    link: ({
      value,
      children,
    }: {
      value: any;
      children: React.ReactNode;
    }): JSX.Element => {
      const isExternal = (value?.href || '').startsWith('http');
      const target = isExternal ? '_blank' : undefined;
      const rel = isExternal ? 'noopener' : value?.rel;
      return (
        <a href={value?.href} target={target} rel={rel} title={value?.title}>
          {children}
        </a>
      );
    },
  },
  block: {
    h1: ({ value, children }: { value: any; children: React.ReactNode }) => (
      <h1
        id={sanitizeFirstChildText(value)}
        style={{
          fontFamily: 'Merriweather',
          fontSize: '32px',
          fontWeight: '400',
          lineHeight: '40px',
          marginTop: '30px',
          marginBottom: '20px',
          color: '#023371',
        }} // Ensure the color is explicitly set
      >
        {children}
      </h1>
    ),
    h2: ({ value, children }: { value: any; children: React.ReactNode }) => (
      <h2
        id={sanitizeFirstChildText(value)}
        style={{
          fontFamily: 'Merriweather',
          fontSize: '20px',
          fontWeight: '400',
          lineHeight: '28px',
          marginTop: '25px',
          marginBottom: '18px',
          color: '#023371',
        }}
      >
        {children}
      </h2>
    ),
    h3: ({ value, children }: { value: any; children: React.ReactNode }) => (
      <h3
        id={sanitizeFirstChildText(value)}
        style={{
          fontFamily: 'Merriweather',
          fontSize: '18px',
          fontWeight: '400',
          lineHeight: '26px',
          marginTop: '20px',
          marginBottom: '15px',
          color: '#023371',
        }}
      >
        {children}
      </h3>
    ),
    h4: ({ value, children }: { value: any; children: React.ReactNode }) => (
      <h4
        id={sanitizeFirstChildText(value)}
        style={{
          fontFamily: 'Merriweather',
          fontSize: '16px',
          fontWeight: '400',
          lineHeight: '24px',
          marginTop: '18px',
          marginBottom: '12px',
          color: '#023371',
        }}
      >
        {children}
      </h4>
    ),
    h5: ({ value, children }: { value: any; children: React.ReactNode }) => (
      <h5
        id={sanitizeFirstChildText(value)}
        style={{
          fontFamily: 'Merriweather',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '22px',
          marginTop: '16px',
          marginBottom: '10px',
          color: '#023371',
        }}
      >
        {children}
      </h5>
    ),
    h6: ({ value, children }: { value: any; children: React.ReactNode }) => (
      <h6
        id={sanitizeFirstChildText(value)}
        style={{
          fontFamily: 'Merriweather',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '22px',
          marginTop: '16px',
          marginBottom: '10px',
          color: '#023371',
        }}
      >
        {children}
      </h6>
    ),
  },
});

const RichTextBody: FC<ElementProps> = (props: ElementProps) => {
  const portableText = transformToPortableText(props.element.value);

  return (
    <PortableText
      value={portableText}
      components={RichTextBodyResolvers(props.element, false)}
    />
  );
};

type RichTextValueProps = Readonly<{
  value: PortableTextBlock[];
  isInsideTable: boolean;
  element: Elements.RichTextElement;
}>;

const RichTextValue: FC<RichTextValueProps> = (props: RichTextValueProps) => (
  <PortableText
    value={props.value}
    components={RichTextBodyResolvers(props.element, props.isInsideTable)}
  />
);

export default RichTextBody;
export { RichTextValue };
